import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../lib/AuthContext";

const AdminProfilePage = () => {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ kind: "exchanging", message: "Signing you in..." });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      const hash = window.location.hash || "";
      const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const sessionId = params.get("session_id");
      if (!sessionId) {
        setState({ kind: "error", message: "No session_id returned from Google." });
        return;
      }

      try {
        const u = await exchangeSession(sessionId);
        // strip the fragment from history
        window.history.replaceState({}, "", window.location.pathname);
        if (!u?.is_admin) {
          setState({
            kind: "forbidden",
            message: `Signed in as ${u?.email || "unknown"}, but that account is not on the AfroAm admin allowlist.`,
          });
          return;
        }
        setState({ kind: "ok", message: `Welcome, ${u.name || u.email}.` });
        setTimeout(() => navigate("/admin/employment", { replace: true }), 700);
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Sign-in failed.";
        setState({
          kind: "error",
          message: typeof detail === "string" ? detail : "Sign-in failed.",
        });
      }
    };
    run();
  }, [exchangeSession, navigate]);

  const Icon =
    state.kind === "ok" ? ShieldCheck : state.kind === "exchanging" ? Loader2 : ShieldAlert;

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-24 min-h-[80vh]">
        <div className="max-w-[820px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="admin-profile-callback"
            className="border border-amber-400/30 bg-[#0a0a0a] p-8 md:p-12"
          >
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
              — Admin · Sign-in
            </div>
            <Icon
              size={42}
              className={`mb-6 ${state.kind === "exchanging" ? "text-amber-400 animate-spin" : state.kind === "ok" ? "text-amber-400" : "text-red-400"}`}
            />
            <h1 className="font-display uppercase text-4xl md:text-5xl text-white leading-[0.9]">
              {state.kind === "ok" && (
                <>
                  Access <span className="text-amber-400">granted.</span>
                </>
              )}
              {state.kind === "exchanging" && (
                <>
                  Verifying your <span className="text-amber-400">account...</span>
                </>
              )}
              {state.kind === "forbidden" && (
                <>
                  Access <span className="text-amber-400">denied.</span>
                </>
              )}
              {state.kind === "error" && (
                <>
                  Sign-in <span className="text-amber-400">failed.</span>
                </>
              )}
            </h1>
            <p className="text-neutral-300 mt-6 leading-relaxed">{state.message}</p>
            {(state.kind === "error" || state.kind === "forbidden") && (
              <a
                href="/admin/employment"
                data-testid="admin-profile-back"
                className="mt-8 inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
              >
                Back to sign-in
              </a>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AdminProfilePage;
